'use client'

import { useRouter } from 'next/navigation'
import type { SocialFollowersFeedQuery, SocialDiscoveryFeedQuery } from '@web/gen/feed'
import { useGetUserBanStatusQuery } from '@web/gen/service/creativefabrica/studio/v2'
import { tw } from 'utils/classnames'
import { ProjectPost } from './_components/project-post'
import * as React from 'react'
import { useFeedSorting } from '@repo/web-ui/ask-cf/contexts/feed-sorting-context'
import { useInView } from 'framer-motion'
import { useHydrated } from 'hooks/use-hydrated'
import {
  useSocialDiscoveryFeedInfiniteQuery,
  useSocialFollowersFeedInfiniteQuery,
} from './_hooks/use-feed-infinite'
import { EmptyFeed } from './_components/empty-feed'
import { SocialToaster } from './_components/social-toaster'
import { PostProjectInputSection } from './_components/post-project-input/post-project-input-section'
import { useUserQuery } from 'auth/hooks'
import { ChooseFeedButton } from './_components/choose-feed-button'
import type { InfiniteData } from '@tanstack/react-query'

type ProjectArray = SocialDiscoveryFeedQuery['socialDiscoveryFeed']['posts']
export type Project = NonNullable<ProjectArray>[number]

export function PageClient({
  isMasterfeed = true,
  initialDiscoveryInitialData,
}: {
  isMasterfeed?: boolean
  initialDiscoveryInitialData?: InfiniteData<SocialDiscoveryFeedQuery>
}) {
  const router = useRouter()

  const hydrated = useHydrated()
  const { data: user } = useUserQuery()
  const { activeSortOption } = useFeedSorting()
  const sentinelRef = React.useRef<HTMLDivElement>(null)
  const isYourFeed = activeSortOption === 'your-feed'

  const getUserBanStatusQuery = useGetUserBanStatusQuery({})

  React.useEffect(() => {
    if (getUserBanStatusQuery.data?.banStatus === 2) {
      router.replace('/')
    }
  }, [getUserBanStatusQuery.data?.banStatus, router])

  const discoveryFeedQuery = useSocialDiscoveryFeedInfiniteQuery({
    initialData: initialDiscoveryInitialData,
  })
  const followersFeedQuery = useSocialFollowersFeedInfiniteQuery()

  const activeQuery = isYourFeed ? followersFeedQuery : discoveryFeedQuery
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = activeQuery

  const isInView = useInView(sentinelRef, { margin: '800px', once: false })

  const projects = React.useMemo(() => {
    if (!data?.pages) {
      return null
    }
    return data.pages.flatMap(page => {
      if (isYourFeed) {
        return (page as SocialFollowersFeedQuery).socialFollowersFeed?.posts ?? []
      }
      return (page as SocialDiscoveryFeedQuery).socialDiscoveryFeed?.posts ?? []
    })
  }, [data, isYourFeed])

  React.useEffect(() => {
    if (isInView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [isInView, hasNextPage, isFetchingNextPage, fetchNextPage])

  if (!hydrated) {
    return (
      <>
        {isMasterfeed ? <PostProjectInputSection slug={user?.me?.user?.slug || ''} /> : null}

        <section className={tw('mx-auto rounded-b-2 relative flex w-full justify-center')}>
          <div className="flex mt-4 tablet:mt-0 flex-col justify-start items-center bg-cf-grey-50 gap-y-6 w-full">
            {(initialDiscoveryInitialData?.pages?.[0]?.socialDiscoveryFeed?.posts ?? []).map(
              project => (
                <div
                  key={project.id}
                  className="flex lg:max-w-[756px] flex-col w-full rounded-[12px] relative bg-cf-white h-fit border border-[#E2E4E9] p-4 tablet:pt-6"
                >
                  <ProjectPost project={project} />
                </div>
              ),
            )}
          </div>
        </section>
      </>
    )
  }

  if (isYourFeed && !projects?.length) {
    return <EmptyFeed onRefresh={() => followersFeedQuery.refetch()} />
  }

  return (
    <>
      {isMasterfeed ? <PostProjectInputSection slug={user?.me?.user?.slug || ''} /> : null}

      {isMasterfeed ? (
        <div className="mb-4 sm:hidden w-full">
          <ChooseFeedButton />
        </div>
      ) : null}

      <section className={tw('mx-auto rounded-b-2 relative flex w-full justify-center')}>
        <div className="flex mt-4 tablet:mt-0 flex-col justify-start items-center bg-cf-grey-50 gap-y-6 w-full">
          {projects?.map((project, index) => (
            <div
              data-index={index}
              key={project.id}
              className="flex lg:max-w-[756px] flex-col w-full rounded-[12px] relative bg-cf-white h-fit border border-[#E2E4E9] p-4 tablet:pt-6"
            >
              <ProjectPost project={project} />
            </div>
          ))}

          {isFetchingNextPage ? (
            <div className="flex justify-center py-4">
              <div style={{ height: 150 }} aria-hidden />
              <div className="text-cf-grey-600 text-14">Loading more posts...</div>
              <div style={{ height: 150 }} aria-hidden />
            </div>
          ) : null}

          <div ref={sentinelRef} className="h-10 w-full" />
        </div>
      </section>

      <SocialToaster />
    </>
  )
}
